import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Send, CheckCircle } from 'lucide-react';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-800'
};

const InvoiceViewer = ({ isOpen, onClose, invoice, onDownloadPDF, onUpdateStatus, companySettings }) => {
  if (!invoice) return null;

  // Default company settings if not provided
  const company = companySettings || {
    name: 'SPT Digital Marketing',
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: ''
  };

  // Format company address for display
  const getCompanyAddress = () => {
    const parts = [];
    if (company.address_line1) parts.push(company.address_line1);
    if (company.address_line2) parts.push(company.address_line2);
    const cityStateZip = [company.city, company.state, company.zip_code].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts;
  };

  // Format due date
  const formatDueDate = (dueDate) => {
    if (!dueDate || dueDate === 'upon_receipt') return 'Due Upon Receipt';
    return new Date(dueDate).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handlePrint = () => {
    // Create a new window with just the invoice content for clean printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    const addressLines = getCompanyAddress();
    
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          /* ========== RESET & BASE ========== */
          *, *::before, *::after {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          html {
            font-size: 10px;
          }
          
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 1rem;
            line-height: 1.5;
            color: #1a1a1a;
            background: #fff;
            padding: 4rem;
            max-width: 800px;
            margin: 0 auto;
          }
          
          /* ========== TYPOGRAPHY ========== */
          .text-xs { font-size: 0.9rem; }
          .text-sm { font-size: 1rem; }
          .text-base { font-size: 1.1rem; }
          .text-lg { font-size: 1.3rem; }
          .text-xl { font-size: 1.6rem; }
          .text-2xl { font-size: 2rem; }
          .text-3xl { font-size: 2.4rem; }
          
          .font-normal { font-weight: 400; }
          .font-medium { font-weight: 500; }
          .font-semibold { font-weight: 600; }
          .font-bold { font-weight: 700; }
          
          .text-gray-400 { color: #9ca3af; }
          .text-gray-500 { color: #6b7280; }
          .text-gray-600 { color: #4b5563; }
          .text-gray-700 { color: #374151; }
          .text-primary { color: #1F3C4F; }
          
          .uppercase { text-transform: uppercase; }
          .tracking-wide { letter-spacing: 0.05em; }
          
          /* ========== LAYOUT ========== */
          .invoice {
            display: flex;
            flex-direction: column;
            gap: 2.4rem;
          }
          
          /* ========== HEADER ========== */
          .header {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 4rem;
            padding-bottom: 2rem;
            border-bottom: 2px solid #1F3C4F;
          }
          
          .company {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
          }
          
          .company-name {
            font-size: 2.4rem;
            font-weight: 700;
            color: #1F3C4F;
            margin-bottom: 0.4rem;
          }
          
          .company-details {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
            color: #4b5563;
            font-size: 1rem;
          }
          
          .invoice-meta {
            text-align: right;
            display: flex;
            flex-direction: column;
            gap: 1.2rem;
          }
          
          .meta-group {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
          }
          
          .meta-label {
            font-size: 0.85rem;
            font-weight: 600;
            color: #4b5563;
            text-transform: uppercase;
            letter-spacing: 0.08em;
          }
          
          .meta-value {
            font-size: 1.1rem;
            color: #1a1a1a;
          }
          
          .invoice-number {
            font-size: 1.4rem;
            font-weight: 700;
            color: #1F3C4F;
            font-variant-numeric: tabular-nums;
          }
          
          /* ========== BILLING SECTION ========== */
          .billing-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
          }
          
          .billing-box {
            padding: 1.6rem;
            background: #f8f9fa;
            border-radius: 0.4rem;
          }
          
          .billing-label {
            font-size: 0.85rem;
            font-weight: 600;
            color: #4b5563;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            margin-bottom: 0.8rem;
          }
          
          .billing-name {
            font-size: 1.3rem;
            font-weight: 600;
            color: #1a1a1a;
            margin-bottom: 0.4rem;
          }
          
          .billing-details {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
            color: #4b5563;
            font-size: 1rem;
          }
          
          .reference-box {
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          
          .reference-value {
            font-size: 1.2rem;
            font-weight: 600;
            color: #1F3C4F;
            font-variant-numeric: tabular-nums;
          }
          
          /* ========== LINE ITEMS TABLE ========== */
          .line-items {
            width: 100%;
            border-collapse: collapse;
          }
          
          .line-items thead {
            border-bottom: 2px solid #1F3C4F;
          }
          
          .line-items th {
            padding: 1rem 1.2rem;
            font-size: 0.9rem;
            font-weight: 600;
            color: #1F3C4F;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            text-align: left;
            background: #f8f9fa;
          }
          
          .line-items th.col-qty,
          .line-items th.col-price,
          .line-items th.col-amount {
            text-align: right;
          }
          
          .line-items th.col-qty { width: 6rem; }
          .line-items th.col-price { width: 10rem; }
          .line-items th.col-amount { width: 10rem; }
          
          .line-items tbody tr {
            border-bottom: 1px solid #e5e7eb;
          }
          
          .line-items td {
            padding: 1.2rem;
            font-size: 1rem;
            vertical-align: top;
            color: #374151;
          }
          
          .line-items td.col-description {
            font-weight: 500;
            color: #1a1a1a;
            line-height: 1.6;
            white-space: pre-wrap;
            word-break: break-word;
          }
          
          .line-items td.col-qty,
          .line-items td.col-price,
          .line-items td.col-amount {
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }
          
          .line-items td.col-amount {
            font-weight: 600;
          }
          
          /* ========== TOTALS ========== */
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-top: 0.8rem;
          }
          
          .totals-table {
            width: 28rem;
            border-collapse: collapse;
          }
          
          .totals-table tr {
            border-bottom: 1px solid #e5e7eb;
          }
          
          .totals-table tr.total-row {
            border-bottom: none;
            border-top: 2px solid #1F3C4F;
          }
          
          .totals-table td {
            padding: 0.8rem 1.2rem;
            font-size: 1rem;
          }
          
          .totals-table td:first-child {
            color: #4b5563;
          }
          
          .totals-table td:last-child {
            text-align: right;
            font-weight: 500;
            font-variant-numeric: tabular-nums;
          }
          
          .totals-table tr.total-row td {
            padding-top: 1.2rem;
            font-size: 1.4rem;
            font-weight: 700;
          }
          
          .totals-table tr.total-row td:first-child {
            color: #1a1a1a;
          }
          
          .totals-table tr.total-row td:last-child {
            color: #1F3C4F;
          }
          
          /* ========== NOTES ========== */
          .notes-section {
            margin-top: 1rem;
            padding: 1.6rem;
            background: #f8f9fa;
            border-left: 4px solid #1F3C4F;
            border-radius: 0 0.4rem 0.4rem 0;
          }
          
          .notes-label {
            font-size: 0.85rem;
            font-weight: 600;
            color: #1F3C4F;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            margin-bottom: 0.6rem;
          }
          
          .notes-content {
            font-size: 1rem;
            color: #4b5563;
            line-height: 1.6;
            white-space: pre-wrap;
          }
          
          /* ========== FOOTER ========== */
          .footer {
            margin-top: 2rem;
            padding-top: 2rem;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 1rem;
          }
          
          /* ========== PRINT STYLES ========== */
          @media print {
            html { font-size: 9px; }
            body { padding: 0; }
            @page { 
              margin: 1.5cm; 
              size: letter; 
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice">
          <!-- Header -->
          <header class="header">
            <div class="company">
              <div class="company-name">${company.name}</div>
              <div class="company-details">
                ${addressLines.map(line => `<span>${line}</span>`).join('')}
                ${company.phone ? `<span>${company.phone}</span>` : ''}
                ${company.email ? `<span>${company.email}</span>` : ''}
              </div>
            </div>
            <div class="invoice-meta">
              <div class="meta-group">
                <span class="meta-label">Invoice Number</span>
                <span class="invoice-number">${invoice.invoice_number}</span>
              </div>
              <div class="meta-group">
                <span class="meta-label">Date</span>
                <span class="meta-value">${new Date(invoice.created_at).toLocaleDateString()}</span>
              </div>
              <div class="meta-group">
                <span class="meta-label">Due Date</span>
                <span class="meta-value">${formatDueDate(invoice.due_date)}</span>
              </div>
            </div>
          </header>
          
          <!-- Billing Section -->
          <section class="billing-section">
            <div class="billing-box">
              <div class="billing-label">Bill To</div>
              <div class="billing-name">${invoice.customer_name || ''}</div>
              <div class="billing-details">
                ${invoice.customer_company ? `<span>${invoice.customer_company}</span>` : ''}
                ${invoice.customer_email ? `<span>${invoice.customer_email}</span>` : ''}
                ${invoice.customer_address ? `<span>${invoice.customer_address}</span>` : ''}
              </div>
            </div>
            <div class="reference-box">
              <div class="billing-label">Reference</div>
              <div class="reference-value">Job #${invoice.job_number}</div>
            </div>
          </section>
          
          <!-- Line Items -->
          <table class="line-items">
            <thead>
              <tr>
                <th class="col-description">Description</th>
                <th class="col-qty">Qty</th>
                <th class="col-price">Unit Price</th>
                <th class="col-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${(invoice.line_items || []).map(item => `
                <tr>
                  <td class="col-description">${item.description}</td>
                  <td class="col-qty">${item.quantity}</td>
                  <td class="col-price">${formatCurrency(item.unit_price)}</td>
                  <td class="col-amount">${formatCurrency(item.amount)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <!-- Totals -->
          <section class="totals-section">
            <table class="totals-table">
              <tr>
                <td>Subtotal</td>
                <td>${formatCurrency(invoice.subtotal)}</td>
              </tr>
              ${invoice.tax_amount > 0 ? `
                <tr>
                  <td>Tax (${invoice.tax_rate}%)</td>
                  <td>${formatCurrency(invoice.tax_amount)}</td>
                </tr>
              ` : ''}
              <tr class="total-row">
                <td>Total</td>
                <td>${formatCurrency(invoice.total)}</td>
              </tr>
            </table>
          </section>
          
          <!-- Notes -->
          ${invoice.notes ? `
            <section class="notes-section">
              <div class="notes-label">Notes</div>
              <div class="notes-content">${invoice.notes}</div>
            </section>
          ` : ''}
          
          <!-- Footer -->
          <footer class="footer">
            Thank you for your business!
          </footer>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    
    // Trigger print after a short delay to ensure content is rendered
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between border-b pb-4">
          <div>
            <DialogTitle className="font-heading text-2xl text-[#1F3C4F]">
              {invoice.invoice_number}
            </DialogTitle>
            <Badge className={STATUS_STYLES[invoice.status]}>
              {invoice.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {invoice.status === 'draft' && onUpdateStatus && (
              <Button variant="outline" size="sm" onClick={() => onUpdateStatus(invoice.id, 'sent')}>
                <Send className="w-4 h-4 mr-2" />
                Mark Sent
              </Button>
            )}
            {invoice.status === 'sent' && onUpdateStatus && (
              <Button variant="outline" size="sm" onClick={() => onUpdateStatus(invoice.id, 'paid')} className="text-green-600 border-green-600 hover:bg-green-50">
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark Paid
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-invoice-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </DialogHeader>
        
        {/* Invoice Content Preview */}
        <div className="py-6" id="invoice-print-area">
          {/* Header */}
          <div className="flex justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-[#1F3C4F]">{company.name}</h1>
              {getCompanyAddress().map((line, idx) => (
                <p key={idx} className="text-slate-600 mt-1">{line}</p>
              ))}
              {company.phone && <p className="text-slate-600 mt-2">{company.phone}</p>}
              {company.email && <p className="text-slate-600">{company.email}</p>}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Invoice Number</p>
              <p className="font-mono text-lg font-semibold">{invoice.invoice_number}</p>
              <p className="text-sm text-slate-500 mt-2">Date</p>
              <p>{new Date(invoice.created_at).toLocaleDateString()}</p>
              <p className="text-sm text-slate-500 mt-2">Due Date</p>
              <p>{formatDueDate(invoice.due_date)}</p>
            </div>
          </div>

          {/* Bill To */}
          <div className="mb-8 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500 mb-1">Bill To</p>
            <p className="font-semibold text-lg">{invoice.customer_name}</p>
            {invoice.customer_company && <p className="text-slate-600">{invoice.customer_company}</p>}
            {invoice.customer_email && <p className="text-slate-600">{invoice.customer_email}</p>}
            {invoice.customer_address && <p className="text-slate-600">{invoice.customer_address}</p>}
          </div>

          {/* Job Reference */}
          <div className="mb-6">
            <p className="text-sm text-slate-500">Reference</p>
            <p className="font-mono text-[#1F3C4F]">Job #{invoice.job_number}</p>
          </div>

          {/* Line Items */}
          <table className="w-full mb-6">
            <thead>
              <tr className="border-b-2 border-[#1F3C4F]">
                <th className="text-left py-3 font-heading">Description</th>
                <th className="text-right py-3 font-heading w-20">Qty</th>
                <th className="text-right py-3 font-heading w-28">Unit Price</th>
                <th className="text-right py-3 font-heading w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items?.map((item, idx) => (
                <tr key={idx} className="border-b border-slate-200">
                  <td className="py-3 whitespace-pre-wrap break-words">{item.description}</td>
                  <td className="py-3 text-right">{item.quantity}</td>
                  <td className="py-3 text-right">{formatCurrency(item.unit_price)}</td>
                  <td className="py-3 text-right font-medium">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
              </div>
              {invoice.tax_amount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-slate-600">Tax ({invoice.tax_rate}%)</span>
                  <span className="font-medium">{formatCurrency(invoice.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t-2 border-[#1F3C4F] mt-2">
                <span className="text-xl font-heading font-bold">Total</span>
                <span className="text-xl font-heading font-bold text-[#1F3C4F]">{formatCurrency(invoice.total)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-2">Notes</p>
              <p className="text-slate-700 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-slate-500">Thank you for your business!</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceViewer;
